<template>
  <div
    class="softphone-widget"
    data-cy="softphone-widget"
  >
    <!--
      The status, and beside it the way out of it.

      Go offline sits up here rather than down with the call controls: those are about the call in
      front of the agent, and this is about the shift - it is how they stop taking calls from
      wherever the panel is open, instead of going back to the campaigns list for it. Small and
      quiet, like Decline and Mute, because it is not the thing to do while a call is on.

      Positioned off the badge's right rather than laid out beside it, so the badge stays centred
      on the panel whatever the button next to it says.
    -->
    <div class="softphone-widget__status">
      <!--
        The microphone and speaker, on the badge's left as Go offline is on its right: both are about
        the agent's setup rather than the call, so they sit up here, out of the way of the call
        controls, and reachable in every state.
      -->
      <SoftphoneAudioDevices class="softphone-widget__audio-devices" />

      <span
        class="softphone-widget__badge"
        :class="statusClass"
        data-cy="softphone-status"
      >
        {{ softphoneStatusLabel }}
      </span>

      <b-button
        class="softphone-widget__go-offline"
        type="button"
        size="sm"
        variant="outline-secondary"
        :disabled="!canGoOffline"
        :title="goOfflineTitle"
        data-cy="softphone-go-offline"
        @click="onGoOffline"
      >
        <b-spinner
          v-if="isGoingOffline"
          small
        />
        <span v-else>Go offline</span>
      </b-button>
    </div>

    <!--
      Two lines, and everything the agent needs before the call itself: what the caller is calling
      about on the first, and on the second the language they will speak and how long the call has
      to run to be billable. The category carries the block - the largest type in the panel, so the
      eye lands on it first - and its qualifiers sit under it, together on one line and at one size,
      because neither of them outranks the other. No punctuation between them: short terms set apart
      by space read faster than a comma list. The campaign name and its terms are the agent's own
      bookkeeping, not something they read mid-call.

      The category gets ONE line: one too long for it is cut with an ellipsis rather than wrapped,
      with the whole of it left in the title, so what is under it never moves down the panel. Two
      lines is the ceiling for the whole block.

      Nothing but a dash until there is a category: a duration with no subject would be a number
      hanging in the air.
    -->
    <div class="softphone-widget__about text-center mt-3">
      <div
        class="softphone-widget__campaign"
        data-cy="softphone-campaign"
      >
        <template v-if="liveCampaignCategoryName">
          <div
            class="softphone-widget__campaign-name"
            data-cy="softphone-campaign-category"
            :title="liveCampaignCategoryName"
          >
            {{ liveCampaignCategoryName }}
          </div>
          <div
            v-if="campaignDetails.length"
            class="softphone-widget__campaign-details"
            data-cy="softphone-campaign-details"
          >
            <span
              v-for="detail in campaignDetails"
              :key="detail.key"
              class="softphone-widget__campaign-detail text-muted"
              :data-cy="`softphone-campaign-${detail.key}`"
            >{{ detail.text }}</span>
          </div>
        </template>
        <template v-else>
          —
        </template>
      </div>
    </div>

    <!--
      Whatever is in it, and no taller.

      This block used to hold the room the tallest of its states needs - the timer with a caller id
      under it - so nothing below it moved when a call arrived. That room is empty most of the time,
      though, and an Offline panel with a band of nothing between the campaign and the buttons reads
      as something that failed to load. So it is sized to what is actually in it, and the buttons sit
      directly under the text until a call gives the block something to say.
    -->
    <div class="softphone-widget__state">
      <!--
        This tab inherited the softphone from one that went away. The campaign is paused and the agent
        is offered it back, rather than this tab registering on its own - see `adoptSoftphoneOwnership`.
        The offer deliberately names nothing: an agent is live for one campaign at a time, and a
        softphone campaign's name is generated, so there is nothing worth putting in front of them.
      -->
      <div
        v-if="hasSoftphoneResumePrompt"
        class="softphone-widget__resume mt-3"
        data-cy="softphone-resume-prompt"
      >
        <div class="small">
          Take calls from here?
        </div>

        <div class="d-flex mt-2">
          <b-button
            class="softphone-widget__resume-action mr-2"
            type="button"
            size="sm"
            variant="success"
            :disabled="isResuming"
            data-cy="softphone-resume"
            @click="onResume"
          >
            <b-spinner
              v-if="isResuming"
              small
            />
            <span v-else>Yes</span>
          </b-button>

          <b-button
            class="softphone-widget__resume-action"
            type="button"
            size="sm"
            variant="outline-secondary"
            :disabled="isResuming"
            data-cy="softphone-dismiss-resume"
            @click="dismissSoftphoneResumePrompt"
          >
            Not now
          </b-button>
        </div>
      </div>

      <!--
        The agent is taking calls, but in a different tab. Moving the softphone here is offered only
        while no call is up: the lock carries the transport, and WebRTC media cannot follow it, so a
        takeover mid-call would hang up on the caller.
      -->
      <div
        v-else-if="isSoftphoneInAnotherTab"
        class="softphone-widget__other-tab mt-3"
        data-cy="softphone-other-tab"
      >
        <div class="small text-muted text-center">
          {{ otherTabMessage }}
        </div>

        <b-button
          class="w-100 mt-2"
          type="button"
          size="sm"
          variant="outline-primary"
          :disabled="!canTakeOverSoftphone || isTakingOver"
          :title="takeOverTitle"
          data-cy="softphone-take-over"
          @click="onTakeOver"
        >
          <b-spinner
            v-if="isTakingOver"
            small
          />
          <span v-else>Take over here</span>
        </b-button>
      </div>

      <!--
        An agent working in another tab, or another application entirely, still gets told a call is
        here. The panel opens itself, the ringtone plays and the browser tab's own title flashes -
        see SoftphoneHeaderWidget, which owns all three.
      -->
      <div
        v-else-if="isRinging"
        class="softphone-widget__incoming mt-3 text-center"
        data-cy="softphone-incoming"
      >
        <font-awesome-icon
          :icon="['fa', 'phone-volume']"
          class="softphone-widget__incoming-icon"
        />

        <div class="softphone-widget__incoming-label mt-1">
          Incoming call
        </div>
      </div>

      <div
        v-else-if="isCallActive"
        class="text-center"
      >
        <div
          class="softphone-widget__timer mt-3"
          data-cy="softphone-call-timer"
        >
          {{ elapsedTime }}
        </div>

        <!--
          Masked until the call reaches the campaign's minimum duration - the point where the call
          becomes billable - and shown in full from the start when the campaign has no minimum. The
          last four digits are enough to confirm who is on the line until then.
        -->
        <div
          v-if="callerId"
          class="softphone-widget__caller-id mt-1"
          :class="{ 'softphone-widget__caller-id--masked': isCallerIdMasked }"
          data-cy="softphone-caller-id"
        >
          {{ callerId }}
        </div>
      </div>

      <div
        v-else-if="isWrappingUp"
        class="softphone-widget__wrap-up mt-3 text-center"
        data-cy="softphone-wrap-up"
      >
        <!--
          The seconds are the only part of this line that changes, and the only part an agent is
          reading - so they are the one part set brighter than the rest, which stays muted as the
          label it is.
        -->
        <div class="text-muted">
          Wrap-up · not taking calls ·
          <span
            class="softphone-widget__wrap-up-seconds"
            data-cy="softphone-wrap-up-seconds"
          >{{ wrapUpSecondsRemaining }}s</span>
        </div>

        <!--
          The countdown is a default, not a sentence. An agent who has finished filing the call
          takes the next one now, and one who needs longer asks for it - both beat the two things
          they do today, which are staring at a clock and going offline to stop it.

          Small and quiet on purpose: these sit inside the state region, not next to Pick up and
          Hang up, because neither of them touches a call.
        -->
        <div class="softphone-widget__wrap-up-actions mt-2">
          <b-button
            class="softphone-widget__wrap-up-action"
            type="button"
            size="sm"
            variant="outline-secondary"
            :disabled="isEndingWrapUp"
            title="Add another 60 seconds before the next call"
            data-cy="softphone-extend-wrap-up"
            @click="onExtendWrapUp"
          >
            Extend
          </b-button>

          <b-button
            class="softphone-widget__wrap-up-action"
            type="button"
            size="sm"
            variant="outline-success"
            :disabled="isEndingWrapUp"
            title="End the wrap-up now and start taking calls again"
            data-cy="softphone-finish-wrap-up"
            @click="onFinishWrapUp"
          >
            <b-spinner
              v-if="isEndingWrapUp"
              small
            />
            <!-- Named for where it puts the agent, not for what it does to the window: Ready is the
                 status the widget lands on, and the badge above says the same word back. -->
            <span v-else>Ready</span>
          </b-button>
        </div>

        <!--
          The call that just ended, while the agent still remembers it. Only for a call Ringba gave an
          id to - without one there is nothing to report against. Neither touches the countdown.

          Sale? No / Yes, placed and styled like Extend / Ready. Yes reports the sale; No only puts the question
          away (per tab). Either way the question is gone once answered - Yes once the report lands, so a
          failed one can be retried. Undo lives in Call Logs alone. Note: the same AgentNoteInput as Call
          Logs, keyed by the call so a new wrap-up starts empty and the old note's pending save still goes out.
        -->
        <div
          v-if="lastEndedCall && lastEndedCall.ringbaCallId"
          class="softphone-widget__wrap-up-disposition mt-3"
          data-cy="softphone-wrap-up-sale"
        >
          <div
            v-if="isSaleQuestionVisible"
            class="softphone-widget__wrap-up-sale-question mb-2"
            data-cy="softphone-wrap-up-sale-question"
          >
            <span class="softphone-widget__wrap-up-label">Sale?</span>

            <b-button
              class="softphone-widget__wrap-up-action"
              type="button"
              size="sm"
              variant="outline-secondary"
              :disabled="isReportingSale"
              title="No sale on this call"
              data-cy="softphone-wrap-up-sale-no"
              @click="dismissedSaleQuestionCallId = lastEndedCall.ringbaCallId"
            >
              No
            </b-button>

            <b-button
              class="softphone-widget__wrap-up-action"
              type="button"
              size="sm"
              variant="outline-success"
              :disabled="isReportingSale"
              title="Report a sale on this call"
              data-cy="softphone-wrap-up-sale-yes"
              @click="onReportSale"
            >
              <b-spinner
                v-if="isReportingSale"
                small
              />
              <span v-else>Yes</span>
            </b-button>
          </div>

          <AgentNoteInput
            :key="lastEndedCall.ringbaCallId"
            class="softphone-widget__wrap-up-note"
            label="Note"
            touch-target
            :account-id="lastEndedCall.accountId || $store.getters['auth/accountId']"
            :call-id="lastEndedCall.ringbaCallId"
            placeholder=""
            data-cy="softphone-wrap-up-note"
          />
        </div>
      </div>

      <!--
        Registered, live, and silent: the account cannot buy calls, either because its balance is
        below the minimum it has to hold or because it has spent the day's budget, so Ringba caps
        the buyer and routes none here - or the campaign itself was paused server-side, by the
        system after unanswered calls or by an administrator.

        Said in a sentence as well as on the badge because it is the one state the agent can neither
        fix nor deduce. Everything else in this panel is either their own doing or something the
        widget is already dealing with; this is somebody else's account, and without the sentence
        the agent sits through a silent shift assuming the phone is broken.

        The wording and the link come from the store - see BLOCKED_NOTICES - because which of the
        two it is decides WHO the agent has to go and find, and that is the only reason the two
        statuses are separate at all.

        Last in the chain on purpose. The status only reaches here between calls, but a call that
        was already being routed can still land - and the ringing branch above must always win.
      -->
      <div
        v-else-if="softphoneBlockedNotice"
        class="softphone-widget__blocked mt-3 text-center"
        data-cy="softphone-blocked-notice"
      >
        <div class="small text-danger">
          {{ softphoneBlockedNotice.text }}
        </div>

        <!-- A paused campaign is ended from here rather than from another page, so its notice
             carries a store action instead of a route. -->
        <b-button
          v-if="softphoneBlockedNotice.action"
          size="sm"
          variant="outline-primary"
          class="mt-2"
          :disabled="isRunningBlockedAction"
          data-cy="softphone-blocked-action"
          @click="onBlockedAction"
        >
          {{ softphoneBlockedNotice.actionText }}
        </b-button>

        <router-link
          v-else-if="softphoneBlockedNotice.routeName && isBlockedNoticeLinkAllowed"
          :to="{ name: softphoneBlockedNotice.routeName, params: softphoneBlockedNotice.routeParams }"
          class="small d-inline-block mt-1"
          data-cy="softphone-blocked-action"
        >
          {{ softphoneBlockedNotice.linkText }}
        </router-link>
      </div>
    </div>

    <!--
      The connection, when it is the thing the agent needs to know about. Muted rather than red:
      nothing has failed yet, the widget is dealing with it, and there is nothing for the agent to
      do - the campaign comes back by itself. A red line here would send them to support for a
      Wi-Fi hop.
    -->
    <div
      v-if="connectionNotice"
      class="softphone-widget__notice mt-3 small text-muted text-center"
      data-cy="softphone-connection-notice"
    >
      {{ connectionNotice }}
    </div>

    <div
      v-if="lastError"
      class="softphone-widget__error mt-3 small text-danger text-center"
      data-cy="softphone-error"
    >
      {{ lastError }}
    </div>

    <div class="softphone-widget__controls">
      <!--
        Plain b-button rather than BaseButtonLoader: that component wraps its button in a
        b-form-group, so the disabled state and the data-cy would land on a fieldset instead of the
        control. These two buttons ARE the softphone - what they are doing has to be unambiguous.
      -->
      <div class="d-flex">
        <b-button
          class="softphone-widget__action mr-2"
          type="button"
          variant="success"
          :disabled="!canPickUp"
          data-cy="softphone-pick-up"
          @click="onPickUp"
        >
          <b-spinner
            v-if="isAnswering"
            small
          />
          <span v-else>Pick up</span>
        </b-button>

        <b-button
          class="softphone-widget__action"
          type="button"
          variant="danger"
          :disabled="!canHangUp"
          data-cy="softphone-hang-up"
          @click="onHangUp"
        >
          <b-spinner
            v-if="isHangingUp"
            small
          />
          <span v-else>Hang up</span>
        </b-button>
      </div>

      <div class="d-flex mt-2">
        <b-button
          class="softphone-widget__action softphone-widget__action--secondary mr-2"
          type="button"
          variant="outline-secondary"
          :disabled="!canDecline"
          data-cy="softphone-decline"
          @click="onDecline"
        >
          Decline
        </b-button>

        <b-button
          class="softphone-widget__action softphone-widget__action--secondary"
          type="button"
          variant="outline-secondary"
          :disabled="!canMute"
          data-cy="softphone-mute"
          @click="onToggleMute"
        >
          <font-awesome-icon
            :icon="['fa', isMuted ? 'microphone-slash' : 'microphone']"
            class="mr-1"
          />
          {{ isMuted ? 'Unmute' : 'Mute' }}
        </b-button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'

import SoftphoneAudioDevices from '@/components/softphone/SoftphoneAudioDevices.vue'
import AgentNoteInput from '@/components/common/AgentNoteInput.vue'
import { getApiErrorMessage, showErrorMessage, showSuccessMessage } from '@/notification-utils'
import { formatCallerId, maskCallerId } from '@/services/softphone/callerId'
import { SOFTPHONE_STATUS } from '@/store/modules/softphone.store'
import { isRouteAllowedByPermission } from '@/router/permissions'

export default {
  name: 'SoftphoneWidget',

  components: {
    SoftphoneAudioDevices,
    AgentNoteInput
  },

  data() {
    return {
      elapsedSeconds: 0,
      wrapUpSecondsRemaining: 0,
      // The wrap-up call whose Sale? question the agent answered No to, in this tab
      dismissedSaleQuestionCallId: null,
      isAnswering: false,
      isHangingUp: false,
      isFinishingWrapUp: false,
      isGoingOffline: false,
      isTakingOver: false,
      isResuming: false,
      isRunningBlockedAction: false,
      tickIntervalId: null
    }
  },

  computed: {
    ...mapState('softphone', [
      'activeCall',
      'incomingCall',
      'isMuted',
      'isReportingSale',
      'isRestoringConnection',
      'lastEndedCall',
      'lastError',
      'status'
    ]),

    /**
     * What the panel says about the connection, and nothing when there is nothing to say.
     *
     * Two different situations, and the difference matters to the agent:
     *
     * - ON A CALL, with the registration gone. The call is unaffected - its media is its own peer
     *   connection and outlives the signalling socket - and the widget waits for the call to end
     *   before rebuilding anything. So the copy must not suggest the call in front of them is at
     *   risk, or an agent will rush a caller off the line for no reason.
     * - RECONNECTING. Nothing for the agent to do and nothing for them to read: the status says it,
     *   the ellipsis says it is still going, and the campaign comes back by itself.
     */
    connectionNotice() {
      if (this.isRestoringConnection) {
        return 'Restoring your connection. Your call is not affected - this finishes after you hang up.'
      }

      if (this.status === SOFTPHONE_STATUS.RECONNECTING) {
        return 'Reconnecting...'
      }

      return ''
    },

    /**
     * Dead from the click until the campaign is live again, and dead too when the countdown runs
     * out on its own - the store sets the same flag either way. The window is over as soon as the
     * resume is in flight, so Extend must not grant a minute the resume takes straight back, and a
     * second Ready must not stack resume requests on one paused campaign.
     */
    isEndingWrapUp() {
      return this.isFinishingWrapUp || this.$store.state.softphone.isEndingWrapUp
    },

    ...mapGetters('auth', ['hasPermission']),

    /**
     * The blocked notice links to the page that lifts the cap - Add funds, for a balance that is too
     * low. A member who may not open that page (a user without the billing permission) is still told
     * what is wrong, but is not sent to a page that would only bounce them to the dashboard.
     */
    isBlockedNoticeLinkAllowed() {
      const notice = this.softphoneBlockedNotice

      if (!notice?.routeName || !this.$router) {
        return true
      }

      const { route } = this.$router.resolve({ name: notice.routeName, params: notice.routeParams })

      return isRouteAllowedByPermission(route, this.hasPermission)
    },

    ...mapGetters('softphone', [
      'callerIdRevealAfterSeconds',
      'canTakeOverSoftphone',
      'hasSoftphoneResumePrompt',
      'isRinging',
      'isCallActive',
      'isSoftphoneBlockedFromCalls',
      'softphoneBlockedNotice',
      'isSoftphoneInAnotherTab',
      'isSoftphoneOnline',
      'isSoftphoneOwner',
      'liveCampaignCategoryName',
      'liveCampaignLanguageName',
      'liveCampaignMinimumDurationInSeconds',
      'softphoneStatusLabel'
    ]),

    /**
     * What sits under the category: the language, written out, and the minimum duration - each only
     * when the campaign has one. The language is the name rather than the code because the agent
     * reads this line at a glance and "Spanish" needs no decoding.
     */
    campaignDetails() {
      const details = []

      if (this.liveCampaignLanguageName) {
        details.push({ key: 'language', text: this.liveCampaignLanguageName })
      }

      if (this.liveCampaignMinimumDurationInSeconds) {
        details.push({ key: 'duration', text: `${this.liveCampaignMinimumDurationInSeconds} seconds` })
      }

      return details
    },

    isWrappingUp() {
      return this.status === SOFTPHONE_STATUS.WRAP_UP
    },

    // Gone once answered: Yes once the sale is reported (shared across tabs), No in this tab
    isSaleQuestionVisible() {
      const call = this.lastEndedCall

      return Boolean(call && call.ringbaCallId) && !call.saleReported && this.dismissedSaleQuestionCallId !== call.ringbaCallId
    },


    otherTabMessage() {
      return this.canTakeOverSoftphone
        ? 'You are taking calls in another tab.'
        : 'A call is in progress in another tab.'
    },

    takeOverTitle() {
      return this.canTakeOverSoftphone
        ? 'Take calls in this tab instead'
        : 'The tab on the call has to finish it first - moving the call here would end it'
    },

    /**
     * Only ever shown on an ANSWERED call - a ringing call deliberately shows nothing but the
     * campaign, so a number never sits on screen for a call nobody has taken.
     */
    callerId() {
      const callerNumber = this.activeCall?.callerNumber

      if (!this.isCallActive || !callerNumber) {
        return ''
      }

      return this.isCallerIdMasked ? maskCallerId(callerNumber) : formatCallerId(callerNumber)
    },

    // Masked until the campaign's minimum duration has elapsed; a campaign without one reveals the
    // number immediately, because `callerIdRevealAfterSeconds` is then 0.
    isCallerIdMasked() {
      return this.elapsedSeconds < this.callerIdRevealAfterSeconds
    },

    /**
     * Exactly the spec: pick up is live only while a call is ringing, hang up only while one is up,
     * and after the call ends neither is.
     *
     * Every control is additionally dead in a mirror. A mirror renders the owner's call faithfully -
     * that is the point of it - but the transport is in the other tab, so a button here would send
     * a request nothing is listening for.
     */
    canPickUp() {
      return this.isSoftphoneOwner && this.isRinging && !this.isAnswering
    },

    canHangUp() {
      return this.isSoftphoneOwner && this.isCallActive && !this.isHangingUp
    },

    canDecline() {
      return this.isSoftphoneOwner && this.isRinging
    },

    /**
     * Go offline is the one control a mirror keeps. It never touches the transport itself - the tab
     * that holds it is the only one that can - it asks that tab to go offline, exactly as the Go
     * Offline button on the campaigns list does from wherever it is clicked.
     *
     * Dead while there is a call on the line. Going offline pauses the campaign and drops the
     * registration, so it would end that call without ever calling itself that: Decline is how the
     * agent refuses a caller and Hang up is how they finish one.
     */
    canGoOffline() {
      return this.isSoftphoneOnline
        && !this.isRinging
        && !this.isCallActive
        && !this.isGoingOffline
    },

    goOfflineTitle() {
      if (this.isRinging) {
        return 'Decline the call first'
      }

      if (this.isCallActive) {
        return 'Hang up first'
      }

      return this.isSoftphoneOnline
        ? 'Stop taking calls and pause your live campaign'
        : 'You are not taking calls'
    },

    canMute() {
      return this.isSoftphoneOwner && this.isCallActive
    },

    // One class per status, so the badge's colour says what the label says. Offline and Other tab
    // share grey: both mean this tab is not taking calls. Reconnecting shares the connecting
    // colour: both are on their way to taking calls, and neither is a failure. The two blocked
    // statuses share the error red: all three leave the agent not taking calls without having
    // asked to stop, and none of them ends until somebody does something about it.
    statusClass() {
      return {
        'softphone-widget__badge--connecting': this.status === SOFTPHONE_STATUS.CONNECTING
          || this.status === SOFTPHONE_STATUS.RECONNECTING,
        'softphone-widget__badge--ready': this.status === SOFTPHONE_STATUS.READY,
        'softphone-widget__badge--ringing': this.isRinging,
        'softphone-widget__badge--active': this.isCallActive,
        'softphone-widget__badge--wrap-up': this.isWrappingUp,
        'softphone-widget__badge--error': this.status === SOFTPHONE_STATUS.ERROR
          || this.isSoftphoneBlockedFromCalls,
        'softphone-widget__badge--offline': this.status === SOFTPHONE_STATUS.OFFLINE
          || this.status === SOFTPHONE_STATUS.OTHER_TAB
      }
    },

    elapsedTime() {
      const minutes = Math.floor(this.elapsedSeconds / 60)
      const seconds = this.elapsedSeconds % 60

      return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
    }
  },

  watch: {
    isCallActive(isActive) {
      if (isActive) {
        this.startTimer()
      } else {
        this.stopTimer()
      }
    },

    isWrappingUp(isWrappingUp) {
      if (isWrappingUp) {
        this.startTimer()
      } else {
        this.stopTimer()
      }
    }
  },

  /**
   * The panel is mounted and unmounted as the agent collapses it, and a call can be up either way,
   * so the timer starts from whatever state is already there rather than from the next transition.
   */
  mounted() {
    if (this.isCallActive || this.isWrappingUp) {
      this.startTimer()
    }
  },

  beforeDestroy() {
    this.stopTimer()
  },

  methods: {
    ...mapActions('softphone', [
      'answerCall',
      'declineCall',
      'dismissSoftphoneResumePrompt',
      'hangUp',
      'requestExtendWrapUp',
      'requestFinishWrapUp',
      'reportSaleForLastCall',
      'requestGoOffline',
      'resumeSoftphoneFromPrompt',
      'setMuted',
      'takeOverSoftphone'
    ]),

    startTimer() {
      this.stopTimer()

      this.tickIntervalId = setInterval(() => {
        const answeredAt = this.activeCall?.answeredAt

        this.elapsedSeconds = answeredAt
          ? Math.max(0, Math.floor((Date.now() - answeredAt) / 1000))
          : 0

        const wrapUpEndsAt = this.$store.state.softphone.wrapUpEndsAt

        this.wrapUpSecondsRemaining = wrapUpEndsAt
          ? Math.max(0, Math.ceil((wrapUpEndsAt - Date.now()) / 1000))
          : 0
      }, 1000)
    },

    stopTimer() {
      if (this.tickIntervalId) {
        clearInterval(this.tickIntervalId)
        this.tickIntervalId = null
      }

      this.elapsedSeconds = 0
      this.wrapUpSecondsRemaining = 0
    },

    async onTakeOver() {
      try {
        this.isTakingOver = true

        const didTakeOver = await this.takeOverSoftphone()

        if (!didTakeOver) {
          showErrorMessage('Could not move the softphone to this tab')
        }
      } catch (error) {
        showErrorMessage('Could not move the softphone to this tab')
      } finally {
        this.isTakingOver = false
      }
    },

    async onResume() {
      try {
        this.isResuming = true
        await this.resumeSoftphoneFromPrompt()
      } catch (error) {
        showErrorMessage(error.response?.data?.title || 'Could not go live again')
      } finally {
        this.isResuming = false
      }
    },

    // The store reports a refused resume through `lastError`, so only an unexpected throw lands here.
    async onBlockedAction() {
      const action = this.softphoneBlockedNotice?.action

      if (!action) {
        return
      }

      try {
        this.isRunningBlockedAction = true
        await this.$store.dispatch(`softphone/${action}`)
      } catch (error) {
        showErrorMessage(error.response?.data?.title || 'Could not resume your campaign')
      } finally {
        this.isRunningBlockedAction = false
      }
    },

    async onPickUp() {
      try {
        this.isAnswering = true
        await this.answerCall()
      } catch (error) {
        showErrorMessage('Could not answer the call')
      } finally {
        this.isAnswering = false
      }
    },

    async onDecline() {
      try {
        await this.declineCall()
      } catch (error) {
        showErrorMessage('Could not decline the call')
      }
    },

    async onHangUp() {
      try {
        this.isHangingUp = true
        await this.hangUp()
      } catch (error) {
        showErrorMessage('Could not hang up the call')
      } finally {
        this.isHangingUp = false
      }
    },

    // The local half of `isEndingWrapUp`: it covers the click itself, including a mirror's, where
    // the store flag belongs to the tab that holds the countdown and lands a beat later.
    async onFinishWrapUp() {
      try {
        this.isFinishingWrapUp = true
        await this.requestFinishWrapUp()
      } catch (error) {
        showErrorMessage('Could not end the wrap-up')
      } finally {
        this.isFinishingWrapUp = false
      }
    },

    /**
     * A failure leaves the circle clickable, so the agent can try again before the wrap-up ends. When the
     * wrap-up ended while the request was out, the panel is gone: say how it went in a toast instead,
     * and on failure point to Call Logs, the only place left to report it from.
     */
    async onReportSale() {
      const ringbaCallId = this.lastEndedCall?.ringbaCallId
      const isPanelGone = () => this.lastEndedCall?.ringbaCallId !== ringbaCallId

      try {
        const isSent = await this.reportSaleForLastCall()

        if (isSent && isPanelGone()) {
          showSuccessMessage('Sale reported')
        }
      } catch (error) {
        const message = getApiErrorMessage(error, 'Could not report the sale')

        showErrorMessage(isPanelGone() ? `${message.replace(/\.$/, '')}. You can still report it from Call Logs.` : message)
      }
    },

    // Nothing but a deadline moving, so it stays clickable: every click is another 60 seconds.
    onExtendWrapUp() {
      this.requestExtendWrapUp()
    },

    /**
     * Same failure message as the campaigns list, and the same silence on success: the status badge
     * and the button itself both say what happened, where the agent is already looking.
     *
     * The panel that hosts this widget closes behind a successful click - see the header. Going
     * offline is the agent saying they are done with the softphone for now, and leaving the panel
     * open in front of them says the opposite. Only on success: a panel that closed over a failure
     * would take the error message with it.
     */
    async onGoOffline() {
      try {
        this.isGoingOffline = true
        await this.requestGoOffline()

        this.$emit('went-offline')
      } catch (error) {
        showErrorMessage('Could not take you offline')
      } finally {
        this.isGoingOffline = false
      }
    },

    async onToggleMute() {
      try {
        await this.setMuted(!this.isMuted)
      } catch (error) {
        showErrorMessage('Could not change the microphone')
      }
    }
  }
}
</script>

<style lang="scss" scoped>
// The Extend / Ready pair; the Sale / Note block below it is sized from the same numbers
$wrap-up-action-width: 6.5rem;
$wrap-up-action-gap: 0.5rem;

.softphone-widget {
  display: flex;
  flex-direction: column;
  flex: 1;
}

// At the foot of the panel, however little is above them.
.softphone-widget__controls {
  margin-top: auto;
  padding-top: 1rem;
}

/*
 * A phone on its side has room across and none down: stacked, a call's panel is about 350px tall
 * against a screen of 293 to 430px under a 55px header. So there it is two columns. The status
 * stays across the top, what the call is about goes on the left, and the call controls go on the
 * right, level with the top of it. The panel then fits the screen in every call state, and Pick up,
 * Hang up, Decline and Mute never have to be scrolled to. Whatever lengthens the left column (the
 * wrap-up's sale question and note, a connection notice, an error) only scrolls under them.
 */
@include short-screen {
  .softphone-widget {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    column-gap: 1.5rem;
    align-content: start;
  }

  .softphone-widget__status {
    grid-row: 1;
    grid-column: 1 / -1;
  }

  .softphone-widget__about {
    grid-row: 2;
    grid-column: 1;
  }

  .softphone-widget__state {
    grid-row: 3;
    grid-column: 1;
  }

  .softphone-widget__notice {
    grid-row: 4;
    grid-column: 1;
  }

  .softphone-widget__error {
    grid-row: 5;
    grid-column: 1;
  }

  // The same 1rem from the status as the campaign beside it.
  .softphone-widget__controls {
    grid-row: 2 / 6;
    grid-column: 2;
    align-self: start;
    margin-top: 1rem;
    padding-top: 0;
  }
}

/*
 * Every control is sized from zero rather than from its own label, so each row is two equal halves
 * and the halves line up down the panel - whatever the buttons in them happen to say. Mute relabels
 * itself to Unmute under the agent, and a control that resizes as it relabels moves the one beside
 * it out from under the pointer.
 *
 * Sized here rather than by Bootstrap's own button sizes: these are the controls an agent hits with
 * a call live in their ear, so they are bigger than a form's buttons, with Decline and Mute a step
 * down from Pick up and Hang up rather than the small type they used to be.
 */
.softphone-widget__action {
  flex: 1 1 0;
  min-width: 0;
  padding: 0.75rem 1rem;
  font-size: 1.125rem;
  font-weight: 600;
}

.softphone-widget__action--secondary {
  padding: 0.5rem 0.75rem;
  font-size: 1rem;
  font-weight: 500;
}

// A line above the wrap-up, as wide as the line above the Sale? question further down (and the
// Extend / Ready pair), so the two read as one set of rules. Drawn rather than a border on the block,
// which would stretch it to the panel's full width.
.softphone-widget__wrap-up::before {
  content: '';
  display: block;
  width: calc(2 * #{$wrap-up-action-width} + #{$wrap-up-action-gap});
  max-width: 100%;
  margin: 0 auto 0.75rem;
  border-top: 1px solid var(--component-custom-input-border-color);
}

/*
 * White against the muted label around it, on the dark panel the widget sits in, so the number
 * carries the line without becoming a second heading - same size, one weight up. Tabular figures
 * because this reads once a second: proportional digits shift the whole line every time the count
 * crosses a 1.
 */
.softphone-widget__wrap-up-seconds {
  color: #fff;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
}

/*
 * The wrap-up pair, deliberately NOT sized like the call controls above: these two do not touch a
 * call, and an agent reaching for Hang up must never find something the same size and shape under
 * their pointer. Equal halves all the same, and narrower than the panel, so the pair reads as one
 * choice about the countdown above it rather than two more buttons.
 *
 * Green on the one that ends the window, because Ready is what it makes the badge above it say, and
 * the Ready badge is green.
 */
.softphone-widget__wrap-up-actions {
  display: flex;
  justify-content: center;
  gap: $wrap-up-action-gap;
}

.softphone-widget__wrap-up-action {
  flex: 0 1 $wrap-up-action-width;
  min-width: 0;
  font-weight: 500;
}

/*
 * Sale? [circle] on the first line, Note [input] on the second, in a block exactly as wide as the
 * Extend / Ready pair above and centred the same way, so both lines start where Extend starts and the
 * note field ends where Ready ends (its saved tick sits just outside).
 */
.softphone-widget__wrap-up-disposition {
  width: calc(2 * #{$wrap-up-action-width} + #{$wrap-up-action-gap});
  max-width: 100%;
  margin-left: auto;
  margin-right: auto;
  // A line between the countdown's controls and the questions about the call that just ended
  padding-top: 0.75rem;
  border-top: 1px solid var(--component-custom-input-border-color);
  text-align: left;
}

// The Sale? and Note labels, one step up from body text so they read at a glance. One width for both,
// so the No button and the note field start at the same x.
.softphone-widget__wrap-up-label,
.softphone-widget__wrap-up-disposition ::v-deep .agent-note-input__label {
  flex: 0 0 auto;
  min-width: 3rem;
  font-size: 1.125rem;
  color: $text-muted;
}

// Label, No, Yes: the answers share what the label leaves, at most the size of Extend / Ready
.softphone-widget__wrap-up-sale-question {
  display: flex;
  align-items: center;
  gap: $wrap-up-action-gap;
}

.softphone-widget__wrap-up-sale-question .softphone-widget__wrap-up-label {
  margin-right: 0.625rem - $wrap-up-action-gap;
}

.softphone-widget__wrap-up-note {
  position: relative;
}

.softphone-widget__wrap-up-note ::v-deep .agent-note-input {
  flex: 1 1 auto;
  min-width: 0;
}

// Out of the line's flow and just past its end, so the field itself runs all the way to Ready's edge
.softphone-widget__wrap-up-note ::v-deep .agent-note-input__saved {
  position: absolute;
  top: 50%;
  left: 100%;
  transform: translateY(-50%);
}

/* Tall enough for the button hung inside it, so the campaign line below keeps its distance. */
.softphone-widget__status {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 2.25rem;
}

/*
 * Qualified by the row so it outranks the component's own `position: relative`. The z-index lifts
 * the menu hung off it over the rest of the panel: the transform makes this its stacking context.
 */
.softphone-widget__status .softphone-widget__audio-devices {
  position: absolute;
  z-index: 5;
  top: 50%;
  left: 0;
  transform: translateY(-50%);
}

.softphone-widget__go-offline {
  position: absolute;
  top: 50%;
  right: 0;
  transform: translateY(-50%);
}

.softphone-widget__badge {
  display: inline-block;
  padding: 0.3rem 0.9rem;
  border-radius: 1rem;
  font-size: 0.875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.softphone-widget__badge--connecting {
  color: #fff;
  background-color: #fd7e14;
}

.softphone-widget__badge--ready {
  color: #fff;
  background-color: #28a745;
}

/* Dark text on both of the pale badges: white on either is unreadable. */
.softphone-widget__badge--ringing {
  color: #084298;
  background-color: #cfe2ff;
}

/* The one badge the same colour as the panel behind it, so it carries a border. */
.softphone-widget__badge--active {
  color: #212529;
  background-color: #fff;
  box-shadow: inset 0 0 0 1px #ced4da;
}

.softphone-widget__badge--wrap-up {
  color: #212529;
  background-color: #ffc107;
}

.softphone-widget__badge--error {
  color: #fff;
  background-color: #dc3545;
}

.softphone-widget__badge--offline {
  color: #fff;
  background-color: #6c757d;
}

/**
 * Equal halves, whatever the labels are. `flex-fill` sizes from the content, so "Yes" came out
 * narrower than "Not now" - and an offer whose two answers are different sizes reads as though one
 * of them is the real one.
 */
.softphone-widget__resume-action {
  flex: 1 1 0;
}

.softphone-widget__campaign {
  font-weight: 600;
}

// The biggest type in the panel, and one line of it: a category too long to fit is cut with an
// ellipsis rather than wrapped, which is what keeps the block to two lines however long a category
// the buyer wrote. The whole of it stays in the title for the agent who wants to read it.
.softphone-widget__campaign-name {
  font-size: 1.5rem;
  font-weight: 600;
  line-height: 1.2;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

// The language and the minimum duration on the second line, side by side and at one size: the
// language the caller speaks and the seconds the call has to run are two facts of the same rank,
// and setting one larger than the other would say otherwise. A campaign without a minimum adds
// nothing here - there is no threshold to watch, and saying so would put a term on screen that
// never changes.
.softphone-widget__campaign-details {
  display: flex;
  justify-content: center;
  gap: 0.75rem;
  white-space: nowrap;
}

.softphone-widget__campaign-detail {
  font-size: 1rem;
  font-weight: 400;
}

.softphone-widget__incoming-icon {
  font-size: 2.5rem;
  animation: softphone-pulse 1s ease-in-out infinite;
}

.softphone-widget__incoming-label {
  font-size: 1.125rem;
  font-weight: 600;
}

.softphone-widget__timer {
  font-size: 2.25rem;
  font-variant-numeric: tabular-nums;
  line-height: 1.2;
}

.softphone-widget__caller-id {
  font-size: 1.125rem;
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.02em;
}

.softphone-widget__caller-id--masked {
  color: #adb5bd;
}

// Every button a finger presses is a 44px target, the smallest it reliably hits. Qualified by the
// widget and last, so it outranks the rules above that size a control from zero.
@include touch-screen {
  .softphone-widget .btn {
    @include touch-target($square: true, $center: true);
  }
}

@keyframes softphone-pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.4; }
}
</style>
